import React, { useEffect, useState } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormGroup,
} from '@mui/material';
import './table.scss';
import { IconDownload, IconUpload } from '../../../icons/Icon';
import UploadModal from '../UploadModal';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../api/axios';
import { BASE_URL } from '../../../constants/apiConstants';
import { toast } from 'react-toastify';
import { getFormattedDate } from '../../../utils/helper'

interface SiteData {
  regionid: any;
  sectorcode: any;
  sectorname: string;
  regionname: string;
  sitecode: string;
  sitename: string;
  isspecialsite: boolean;
  ismappedcalendar: boolean;
  excelData: any[];
}

interface HolidayCalendarProps {
  sites: SiteData[];
  excelData: any[];
  handleUpload?: any;
}

const tableHead = [
  { name: 'Site Code' },
  { name: 'Site Name' },
  { name: 'Mapped Calendar' },
  { name: 'Action' },
];

const HolidayCalendarTable: React.FC<HolidayCalendarProps> = ({ sites, handleUpload }) => {
  const [openModal, setOpenModal] = useState(false); // Track which modal is open
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [holidayData, setHolidayData] = useState<any[]>([]);

  // Function to close modal
  const handleClose = () => {
    setOpenModal(false); // Close the modal
  };

  // Function to open upload modal for specific row
  const handleUploadClick = (sitecode: string) => {
    setOpenModal(true); // Open the modal for the clicked row
    setSelectedSite(sitecode);
  };

  const handleFileUploadWithClose = async (e: React.ChangeEvent<HTMLInputElement>, sitecode: string) => {
    handleUpload(e, sitecode);
    handleClose();
  };

  const handleDownload = async (sitecode: string, sitename: string) => {
    const payload = {
      sitecode: sitecode,
    };
    try {
      const res = await axiosInstance.post<any>(`${BASE_URL}/api/v1/AdminConfiguration/GetHolidaysBySite`, payload);
      handleDownloadCalendar(res?.data?.data?.holidaylist, sitename)
      toast("Holiday calendar downloaded successfully", { type: "success" });
    } catch (err) {
      toast("Failed to download holiday calendar", { type: "error" });
    }
  };


  // Function to download the holiday calendar of a site
  const handleDownloadCalendar = (holidayData: any[], sitename: string) => {
    const data = [['S. No.', 'Holiday Name', 'Date']];

    holidayData.map((holiday: any, i: number) =>
      data.push([`${i + 1}`, holiday.holidayname, getFormattedDate(holiday.holidaydate, 'dd-MM-yyyy')])
    )
    // Create a worksheet from the data
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Holiday Calendar');
    XLSX.writeFile(wb, `holiday_${sitename}.xlsx`);
  };

  return (
    <>
      <FormGroup className="table-wrapper HolidayCalendarTable">
        <TableContainer component={Paper} className="table-inner" sx={{ maxHeight: 422 }}>
          <MuiTable stickyHeader>
            <TableHead>
              <TableRow>
                {tableHead.map((col, index) => (
                  <TableCell key={'holiday-th' + index}>{col?.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.length !== 0 ? (
                sites.map((row, index) => (
                  <TableRow key={'holiday-tr' + index}>
                    <TableCell>{row?.sitecode}</TableCell>
                    <TableCell>{row?.sitename}</TableCell>
                    <TableCell>{row.ismappedcalendar ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <div className="flex justify-content-between align-items-center">
                        <button
                          className="link flex align-items-center"
                          onClick={() => handleUploadClick(row?.sitecode)}
                        >
                          <IconUpload />
                          <span>Upload Holiday Calendar</span>
                        </button>
                        {row.ismappedcalendar && (
                          <button className="link flex align-items-center" onClick={() => handleDownload(row?.sitecode, row?.sitename)}>
                            <IconDownload />
                            <span>Download</span>
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={tableHead.length} className="grey-bg center">
                    No data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </FormGroup>

      {/* Render modal for upload */}
      {openModal && (
        <UploadModal
          open={openModal}
          title="Upload Holiday Calendar"
          discription="Please note that once the Holiday Calendar is uploaded, it will be synchronized and replicated at the current location, ensuring consistent application of holiday schedules."
          handleClose={handleClose}
          handleFileUpload={(e: any) => handleFileUploadWithClose(e, selectedSite)} // Pass the specific index
        />
      )}
    </>
  );
};

export default HolidayCalendarTable;
